import { LicensePlan } from '../types/license'
import { OwnerRegistration } from '../types/onboardingRegistration'
import { trackCrmInterestApi } from '../api/onboardingApi'

/**
 * Tracks interest in HubSpot via the backend (full owner profile + plan),
 * then opens the meeting URL. Falls back to a static URL if the call fails.
 */
export async function openHubSpotLink(options: {
  plan?: LicensePlan
  source: 'request-demo' | 'plan-select'
  registration?: Partial<OwnerRegistration>
}): Promise<void> {
  const email = options.registration?.professionalEmail
  if (!email) {
    window.open(buildFallbackHubSpotUrl(options), '_blank', 'noopener,noreferrer')
    return
  }

  try {
    const result = await trackCrmInterestApi({
      email,
      source: options.source,
      plan: options.plan,
      firstName: options.registration?.firstName,
      lastName: options.registration?.lastName,
      phone: options.registration?.phone,
      jobTitle: options.registration?.jobTitle,
      companyName: options.registration?.companyName,
      companyAddress: options.registration?.companyAddress,
      country: options.registration?.country,
      need: options.registration?.need,
      username: options.registration?.username,
    })
    window.open(result.meetingUrl, '_blank', 'noopener,noreferrer')
  } catch {
    window.open(buildFallbackHubSpotUrl(options), '_blank', 'noopener,noreferrer')
  }
}

function buildFallbackHubSpotUrl(options: {
  plan?: LicensePlan
  source: 'request-demo' | 'plan-select'
  registration?: Partial<OwnerRegistration>
}): string {
  const baseUrl =
    process.env.REACT_APP_HUBSPOT_MEETING_URL ||
    'https://meetings-eu1.hubspot.com/incyia/demo'

  const params = new URLSearchParams()
  const r = options.registration
  if (r?.professionalEmail) params.set('email', r.professionalEmail)
  if (r?.firstName) params.set('firstname', r.firstName)
  if (r?.lastName) params.set('lastname', r.lastName)
  if (r?.companyName) params.set('company', r.companyName)
  if (r?.phone) params.set('phone', r.phone)
  if (r?.jobTitle) params.set('jobtitle', r.jobTitle)
  if (r?.country) params.set('country', r.country)
  if (r?.need) params.set('need', r.need)
  if (r?.username) params.set('username', r.username)
  if (r?.companyAddress) params.set('address', r.companyAddress)
  if (options.plan) {
    params.set('plan', options.plan)
    const planLabel =
      options.plan === 'basic'
        ? 'Standard'
        : options.plan === 'business'
          ? 'Business'
          : options.plan === 'premium'
            ? 'Premium'
            : options.plan
    params.set('plan_interest', planLabel)
  }
  params.set('source', options.source)

  const query = params.toString()
  return query ? `${baseUrl}?${query}` : baseUrl
}

/** @deprecated Use openHubSpotLink instead */
export function buildHubSpotUrl(options: {
  plan?: LicensePlan
  source: 'request-demo' | 'plan-select'
  registration?: Partial<OwnerRegistration>
}): string {
  return buildFallbackHubSpotUrl(options)
}
